import { tv, type VariantProps } from "tailwind-variants"
import {
  type TextColorVariantProps,
  textColorVariants,
} from "../../styles/utils/color"
import { classNames } from "../../utils/classNames"
import { SkeletonLine, type SkeletonProps } from "../Skeleton"
import { Slot, type SlotProps } from "../Slot"

export type TextDisplayProps = SlotProps<"span"> &
  VariantProps<typeof displayVariants> &
  TextColorVariantProps &
  Required<Pick<VariantProps<typeof displayVariants>, "size">>

const displayVariants = tv({
  base: "font-medium leading-tight",
  variants: {
    size: {
      sm: "text-display-sm",
      md: "text-display-md",
      lg: "text-display-lg",
      xl: "text-display-xl",
    },
  },
})

/**
 * Use Display for H1 or H2 Style headers that require lots of emphasis.
 * Some uses are home page, Marketing page hero header, pages with a single lg heading.
 */
function TextDisplay({
  asChild,
  className,
  color,
  size,
  ...rest
}: TextDisplayProps) {
  const Comp = asChild ? Slot : "span"

  return (
    <Comp
      className={classNames(
        displayVariants({ size }),
        textColorVariants({ color }),
        className,
      )}
      {...rest}
    />
  )
}

const textDisplaySkeletonVariants = tv({
  variants: {
    size: {
      sm: "h-6",
      md: "h-8",
      lg: "h-12",
      xl: "h-[60px]",
    },
  },
})

const TextDisplaySkeleton = ({
  className,
  size,
  ...rest
}: SkeletonProps & Pick<TextDisplayProps, "size" | "className">) => {
  return (
    <SkeletonLine
      className={classNames(textDisplaySkeletonVariants({ size }), className)}
      {...rest}
    />
  )
}

export { TextDisplay, TextDisplaySkeleton }
